import React from 'react';
import PropTypes from 'prop-types';
import PictureDaySignup from '../PictureDaySignup';
import styles from './BeforePictureDay.module.scss';

// The entry screen for a school whose fall picture day HAS NOT HAPPENED YET.
//
// WHO THIS IS FOR, which is the thing an earlier design got wrong. Nearly
// everyone who reaches this page is a current-season family wanting THIS
// season's pictures, and many arrive conditioned by other companies
// (Lifetouch) to prepay online before picture day. We do not sell pre-orders --
// photos are taken, then families are texted and emailed a link -- so the job
// of this screen is to say what will actually happen and take their contact
// details, not to offer them a box to type a code into.
//
// So there are NO access-code fields here. The screen that has them is the one
// this school gets AFTER its picture day. The parent holding a code from an
// earlier shoot is a very small minority, and this screen is deliberately not
// where they are served.
//
// Dates are formatted here rather than server-side because the page is
// bilingual and a month name has to follow the reader's language.
const STRINGS = {
  en: {
    heading: 'Pictures will be taken on',
    reassure: 'Every student will be photographed. Nothing to do in advance.',
    preview: 'You’ll see your student’s actual photos here before you choose a package.',
    offer: (terms, date) => `When your photos are ready you’ll have until ${date} to receive ${terms}.`,
    percent: (n) => `${n}% off`,
    amount: (n) => `$${n} off`,
    freeShipping: 'free shipping',
    and: ' and ',
  },
  es: {
    heading: 'Las fotos se tomarán el',
    reassure: 'Todos los estudiantes serán fotografiados. No hay que hacer nada por adelantado.',
    preview: 'Verá las fotos reales de su estudiante aquí antes de elegir un paquete.',
    offer: (terms, date) => `Cuando sus fotos estén listas tendrá hasta el ${date} para recibir ${terms}.`,
    percent: (n) => `${n}% de descuento`,
    amount: (n) => `$${n} de descuento`,
    freeShipping: 'envío gratis',
    and: ' y ',
  },
};

// An ISO date names a CALENDAR DAY. Splitting it by hand rather than letting
// `new Date('2026-09-11')` parse it, because that reads as UTC midnight and
// renders as the 10th for every reader west of Greenwich -- which is all of
// them. The same off-by-one this app has already shipped once server-side.
export const formatDate = (iso, lang) => {
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year, month - 1, day).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
};

// Composed from the coupon's structure rather than a stored sentence, so it
// follows the reader's language and cannot promise terms the coupon does not
// give. Renders nothing when there is nothing to promise.
export const offerTerms = (offer, t) => {
  const parts = [];
  if (offer.percentOff) parts.push(t.percent(offer.percentOff));
  if (offer.amountOff) parts.push(t.amount(offer.amountOff));
  if (offer.freeShipping) parts.push(t.freeShipping);

  return parts.join(t.and);
};

const BeforePictureDay = ({
  pictureDays, schoolId, lang, onSignedUp,
}) => {
  const t = STRINGS[lang] || STRINGS.en;
  const offer = pictureDays?.offer;
  const terms = offer ? offerTerms(offer, t) : '';

  return (
    <div className={styles.plan}>
      <div className={styles.dateBlock}>
        <p className={styles.heading}>{t.heading}</p>
        {/* The one thing a parent came for, set large enough to survive a
            glance at arm's length on a phone. */}
        <p className={styles.date}>{formatDate(pictureDays?.fallPictureDay, lang)}</p>
      </div>

      <p className={styles.reassure}>{t.reassure}</p>
      <p className={styles.preview}>{t.preview}</p>

      {/* The confirmation is handed up: the whole card becomes a thank-you
          rather than a line under a form the parent has finished with. */}
      <PictureDaySignup
        schoolId={schoolId}
        lang={lang}
        confirmInline={false}
        onSignedUp={onSignedUp}
      />

      {/* Only when the coupon actually answered. An offer line with no
          deadline, or terms we cannot state, is a promise checkout may not
          honour -- so it is omitted rather than softened. */}
      {offer && terms && (
        <p className={styles.offer}>
          {t.offer(terms, formatDate(offer.expiresOn, lang))}
        </p>
      )}
    </div>
  );
};

BeforePictureDay.propTypes = {
  pictureDays: PropTypes.shape({
    fallPictureDay: PropTypes.string,
    offer: PropTypes.shape({
      expiresOn: PropTypes.string,
      percentOff: PropTypes.number,
      amountOff: PropTypes.number,
      freeShipping: PropTypes.bool,
    }),
  }),
  schoolId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lang: PropTypes.string,
  onSignedUp: PropTypes.func,
};

BeforePictureDay.defaultProps = { pictureDays: null, lang: 'en', onSignedUp: null };

export default BeforePictureDay;
